import { useContext, useMemo, useState } from 'react';

import ProfileDataContext from '@/context/ProfileDataContext';
import useTranslations from '@/i18n/useTranslations';
import GoBackButton from '@/shared-components/buttons/components/GoBackButton';

import '../../artist-help/profile/styles.css';

export default function ProfileGalleryHelpPaymentAndInvoicesPage() {
  const t = useTranslations('galleries_help.payment_and_invoices');
  const { profileData } = useContext(ProfileDataContext);
  const [openSections, setOpenSections] = useState({
    overview: false,
    paymentsOriginals: false,
    paymentsLimitedEditions: false,
    paymentsSubscriptions: false,
    invoiceAreas: false,
    stripeCosts: false,
    understandingVat: false,
  });

  const galleryCountry = useMemo(() => {
    const raw =
      profileData?.gallery_address?.country
      ?? profileData?.gallery_address?.address_country
      ?? '';
    return typeof raw === 'string' && raw.trim() ? raw.trim() : t('sections.understanding_vat.setup.not_provided');
  }, [profileData, t]);

  const hasVatId = useMemo(() => {
    return Boolean(
      typeof profileData?.gallery_tax_id === 'string'
      && profileData.gallery_tax_id.trim()
    );
  }, [profileData?.gallery_tax_id]);

  const vatVerified = useMemo(() => {
    return Boolean(profileData?.gallery_tax_enabled && hasVatId);
  }, [hasVatId, profileData?.gallery_tax_enabled]);

  const galleryCountryKey = useMemo(() => galleryCountry.toLowerCase(), [galleryCountry]);

  const setupGuidanceKey = useMemo(() => {
    if (galleryCountryKey === 'spain' || galleryCountryKey === 'es') {
      return 'spain';
    }
    if (vatVerified) {
      return 'eu_verified_vat';
    }
    if (hasVatId) {
      return 'vat_present_not_verified';
    }
    return 'default';
  }, [galleryCountryKey, hasVatId, vatVerified]);

  function toggleSection(sectionKey) {
    setOpenSections((previous) => ({
      ...previous,
      [sectionKey]: !previous[sectionKey],
    }));
  }

  return (
    <section className="artist-help-category gallery-help-category">
      <header className="artist-help-category__header gallery-help-category__header">
        <GoBackButton alternativeRoute="/profile/gallery-help" />
        <h1>{t('title')}</h1>
      </header>

      <div className="artist-help-category__body gallery-help-category__body">
        <section className="artist-help-category__card artist-help-category__accordion gallery-help-category__card gallery-help-category__accordion">
          <button
            type="button"
            className="artist-help-category__accordion-trigger gallery-help-category__accordion-trigger"
            onClick={() => toggleSection('overview')}
            aria-expanded={openSections.overview}
          >
            <h2>{t('sections.stripe_connect.title')}</h2>
            <span
              className={`artist-help-category__accordion-icon gallery-help-category__accordion-icon ${openSections.overview ? 'open' : ''}`}
              aria-hidden
            />
          </button>

          {openSections.overview && (
            <div className="artist-help-category__accordion-content gallery-help-category__accordion-content">
              <p>{t('sections.stripe_connect.p1')}</p>

              <div className="artist-help-category__stack gallery-help-category__stack">
                <h3>{t('sections.stripe_connect.how_it_works_title')}</h3>
                <ul>
                  <li>{t('sections.stripe_connect.how_it_works_list_1')}</li>
                  <li>{t('sections.stripe_connect.how_it_works_list_2')}</li>
                  <li>{t('sections.stripe_connect.how_it_works_list_3')}</li>
                </ul>
              </div>

              <div className="artist-help-category__stack gallery-help-category__stack">
                <h3>{t('sections.stripe_connect.where_to_set_up_title')}</h3>
                <p>{t('sections.stripe_connect.where_to_set_up_p1')}</p>
              </div>

              <div className="artist-help-category__stack gallery-help-category__stack">
                <h3>{t('sections.stripe_connect.business_details_title')}</h3>
                <p>{t('sections.stripe_connect.business_details_p1')}</p>
                <p>{t('sections.stripe_connect.business_details_p2')}</p>
              </div>

              <div className="artist-help-category__stack gallery-help-category__stack">
                <h3>{t('sections.stripe_connect.unsupported_country_title')}</h3>
                <p>{t('sections.stripe_connect.unsupported_country_p1')}</p>
              </div>

              <div className="artist-help-category__stack gallery-help-category__stack">
                <h3>{t('sections.stripe_connect.good_to_know_title')}</h3>
                <ul>
                  <li>{t('sections.stripe_connect.good_to_know_list_1')}</li>
                  <li>{t('sections.stripe_connect.good_to_know_list_2')}</li>
                </ul>
              </div>

              <div className="artist-help-category__stack gallery-help-category__stack">
                <h3>{t('sections.stripe_connect.verification_and_payouts_title')}</h3>
                <ul>
                  <li>{t('sections.stripe_connect.verification_and_payouts_list_1')}</li>
                  <li>{t('sections.stripe_connect.verification_and_payouts_list_2')}</li>
                  <li>{t('sections.stripe_connect.verification_and_payouts_list_3')}</li>
                </ul>
              </div>

              <div className="artist-help-category__stack gallery-help-category__stack">
                <h3>{t('sections.stripe_connect.security_title')}</h3>
                <p>{t('sections.stripe_connect.security_p1')}</p>
              </div>
            </div>
          )}
        </section>

        <section className="artist-help-category__card artist-help-category__accordion gallery-help-category__card gallery-help-category__accordion">
          <button
            type="button"
            className="artist-help-category__accordion-trigger gallery-help-category__accordion-trigger"
            onClick={() => toggleSection('understandingVat')}
            aria-expanded={openSections.understandingVat}
          >
            <h2>{t('sections.understanding_vat.title')}</h2>
            <span
              className={`artist-help-category__accordion-icon gallery-help-category__accordion-icon ${openSections.understandingVat ? 'open' : ''}`}
              aria-hidden
            />
          </button>

          {openSections.understandingVat && (
            <div className="artist-help-category__accordion-content gallery-help-category__accordion-content">
              <p>{t('sections.understanding_vat.p1')}</p>

              <div className="artist-help-category__stack gallery-help-category__stack">
                <h3>{t('sections.understanding_vat.two_events.title')}</h3>
                <ul>
                  <li>{t('sections.understanding_vat.two_events.list_1')}</li>
                  <li>{t('sections.understanding_vat.two_events.list_2')}</li>
                </ul>
              </div>

              <div className="artist-help-category__stack gallery-help-category__stack">
                <h3>{t('sections.understanding_vat.tax_snapshot.title')}</h3>
                <p>{t('sections.understanding_vat.tax_snapshot.p1')}</p>
                <p>{t('sections.understanding_vat.tax_snapshot.p2')}</p>
              </div>

              <div className="artist-help-category__stack gallery-help-category__stack">
                <h3>{t('sections.understanding_vat.examples.title')}</h3>
                <ul>
                  <li>{t('sections.understanding_vat.examples.list_1')}</li>
                  <li>{t('sections.understanding_vat.examples.list_2')}</li>
                  <li>{t('sections.understanding_vat.examples.list_3')}</li>
                  <li>{t('sections.understanding_vat.examples.list_4')}</li>
                </ul>
              </div>

              <div className="artist-help-category__stack gallery-help-category__stack">
                <h3>{t('sections.understanding_vat.setup.title')}</h3>
                <p>{t('sections.understanding_vat.setup.country', { country: galleryCountry })}</p>
                <p>
                  {t('sections.understanding_vat.setup.vat_id', {
                    status: vatVerified
                      ? t('sections.understanding_vat.setup.vat_status.verified')
                      : hasVatId
                        ? t('sections.understanding_vat.setup.vat_status.added')
                        : t('sections.understanding_vat.setup.vat_status.not_added'),
                  })}
                </p>
                <p>{t(`sections.understanding_vat.setup.guidance.${setupGuidanceKey}`)}</p>
              </div>

              <p>{t('sections.understanding_vat.footer')}</p>
            </div>
          )}
        </section>

        <section className="artist-help-category__card artist-help-category__accordion gallery-help-category__card gallery-help-category__accordion">
          <button
            type="button"
            className="artist-help-category__accordion-trigger gallery-help-category__accordion-trigger"
            onClick={() => toggleSection('paymentsOriginals')}
            aria-expanded={openSections.paymentsOriginals}
          >
            <h2>{t('sections.payments_originals.title')}</h2>
            <span
              className={`artist-help-category__accordion-icon gallery-help-category__accordion-icon ${openSections.paymentsOriginals ? 'open' : ''}`}
              aria-hidden
            />
          </button>

          {openSections.paymentsOriginals && (
            <div className="artist-help-category__accordion-content gallery-help-category__accordion-content">
              <p>{t('sections.payments_originals.p1')}</p>
              <p>{t('sections.payments_originals.p2')}</p>
            </div>
          )}
        </section>

        <section className="artist-help-category__card artist-help-category__accordion gallery-help-category__card gallery-help-category__accordion">
          <button
            type="button"
            className="artist-help-category__accordion-trigger gallery-help-category__accordion-trigger"
            onClick={() => toggleSection('paymentsLimitedEditions')}
            aria-expanded={openSections.paymentsLimitedEditions}
          >
            <h2>{t('sections.payments_limited_editions.title')}</h2>
            <span
              className={`artist-help-category__accordion-icon gallery-help-category__accordion-icon ${openSections.paymentsLimitedEditions ? 'open' : ''}`}
              aria-hidden
            />
          </button>

          {openSections.paymentsLimitedEditions && (
            <div className="artist-help-category__accordion-content gallery-help-category__accordion-content">
              <p>{t('sections.payments_limited_editions.p1')}</p>
              <p>{t('sections.payments_limited_editions.p2')}</p>
            </div>
          )}
        </section>

        <section className="artist-help-category__card artist-help-category__accordion gallery-help-category__card gallery-help-category__accordion">
          <button
            type="button"
            className="artist-help-category__accordion-trigger gallery-help-category__accordion-trigger"
            onClick={() => toggleSection('paymentsSubscriptions')}
            aria-expanded={openSections.paymentsSubscriptions}
          >
            <h2>{t('sections.payments_subscriptions.title')}</h2>
            <span
              className={`artist-help-category__accordion-icon gallery-help-category__accordion-icon ${openSections.paymentsSubscriptions ? 'open' : ''}`}
              aria-hidden
            />
          </button>

          {openSections.paymentsSubscriptions && (
            <div className="artist-help-category__accordion-content gallery-help-category__accordion-content">
              <p>{t('sections.payments_subscriptions.p1')}</p>
              <p>{t('sections.payments_subscriptions.p2')}</p>
            </div>
          )}
        </section>

        <section className="artist-help-category__card artist-help-category__accordion gallery-help-category__card gallery-help-category__accordion">
          <button
            type="button"
            className="artist-help-category__accordion-trigger gallery-help-category__accordion-trigger"
            onClick={() => toggleSection('invoiceAreas')}
            aria-expanded={openSections.invoiceAreas}
          >
            <h2>{t('sections.invoice_areas.title')}</h2>
            <span
              className={`artist-help-category__accordion-icon gallery-help-category__accordion-icon ${openSections.invoiceAreas ? 'open' : ''}`}
              aria-hidden
            />
          </button>

          {openSections.invoiceAreas && (
            <div className="artist-help-category__accordion-content gallery-help-category__accordion-content">
              <div className="artist-help-category__stack gallery-help-category__stack">
                <h3>{t('sections.invoice_areas.suarte_invoices.title')}</h3>
                <p>{t('sections.invoice_areas.suarte_invoices.p1')}</p>
                <p>{t('sections.invoice_areas.suarte_invoices.p2')}</p>
              </div>

              <div className="artist-help-category__stack gallery-help-category__stack">
                <h3>{t('sections.invoice_areas.add_invoices.title')}</h3>
                <p>{t('sections.invoice_areas.add_invoices.p1')}</p>
                <p>{t('sections.invoice_areas.add_invoices.p2')}</p>
              </div>
            </div>
          )}
        </section>

        <section className="artist-help-category__card artist-help-category__accordion gallery-help-category__card gallery-help-category__accordion">
          <button
            type="button"
            className="artist-help-category__accordion-trigger gallery-help-category__accordion-trigger"
            onClick={() => toggleSection('stripeCosts')}
            aria-expanded={openSections.stripeCosts}
          >
            <h2>{t('sections.stripe_costs.title')}</h2>
            <span
              className={`artist-help-category__accordion-icon gallery-help-category__accordion-icon ${openSections.stripeCosts ? 'open' : ''}`}
              aria-hidden
            />
          </button>

          {openSections.stripeCosts && (
            <div className="artist-help-category__accordion-content gallery-help-category__accordion-content">
              <p>{t('sections.stripe_costs.p1')}</p>
            </div>
          )}
        </section>
      </div>
    </section>
  );
}
